import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Scissors, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import { fmtMoney } from "@/components/dashboard/utils";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SplitLineDialog — splits a line item into two, proportionally dividing
 * quantity, value, and procurement spend.
 *
 * Props:
 *   open, onOpenChange  — Dialog open state
 *   item                — the parent item being split
 *   allItems            — full items list (used for autofill + collision check)
 *   onSplitSuccess      — callback(newChildItem) fired after a successful split
 */
export default function SplitLineDialog({ open, onOpenChange, item, allItems, onSplitSuccess }) {
  const [splitQty, setSplitQty]             = useState("");
  const [newLineNumber, setNewLineNumber]   = useState("");
  const [submitting, setSubmitting]         = useState(false);
  const [apiError, setApiError]             = useState("");

  // Autofill the first free ".N" suffix whenever the dialog opens / item changes
  useEffect(() => {
    if (!open || !item) return;
    setSplitQty("");
    setApiError("");
    const poItems = allItems.filter(i => i.po_number === item.po_number);
    const taken   = new Set(poItems.map(i => i.line_number));
    let candidate = null;
    for (let n = 1; n <= 99; n++) {
      const c = `${item.line_number}.${n}`;
      if (!taken.has(c)) { candidate = c; break; }
    }
    setNewLineNumber(candidate || `${item.line_number}.1`);
  }, [open, item, allItems]);

  const parentQty = Number(item?.quantity) || 0;
  const qty       = Number(splitQty) || 0;

  // Mirror backend math exactly (floor to 2 decimal places, parent absorbs remainder)
  const floor2 = (v) => Math.floor((Number(v) || 0) * 100) / 100;

  const ratio         = parentQty > 0 ? qty / parentQty : 0;
  const childValue    = floor2((item?.value_usd    || 0) * ratio);
  const childMaterial = floor2((item?.material_spend || 0) * ratio);
  const childTooling  = floor2((item?.tooling_spend  || 0) * ratio);
  const childGauging  = floor2((item?.gauging_spend  || 0) * ratio);

  const newParentQty      = parentQty - qty;
  const newParentValue    = +((item?.value_usd    || 0) - childValue).toFixed(2);
  const newParentMaterial = +((item?.material_spend || 0) - childMaterial).toFixed(2);
  const newParentTooling  = +((item?.tooling_spend  || 0) - childTooling).toFixed(2);
  const newParentGauging  = +((item?.gauging_spend  || 0) - childGauging).toFixed(2);

  // Collision check for the new line number
  const lineCollides = useMemo(() => {
    if (!newLineNumber.trim() || !item) return false;
    return allItems.some(
      i => i.po_number === item.po_number && i.line_number === newLineNumber.trim()
    );
  }, [newLineNumber, allItems, item]);

  const canSubmit = (
    qty > 0 &&
    qty < parentQty &&
    newLineNumber.trim() !== "" &&
    !lineCollides &&
    !submitting
  );

  const handleSubmit = async () => {
    if (!canSubmit || !item) return;
    setSubmitting(true);
    setApiError("");
    try {
      const token = localStorage.getItem("rl_token");
      const res = await axios.post(
        `${API}/api/line-items/${item.id}/split`,
        { split_qty: qty, new_line_number: newLineNumber.trim() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success(`Line split successfully · created Line ${newLineNumber.trim()}`);
      onSplitSuccess?.(res.data);
    } catch (e) {
      setApiError(e?.response?.data?.detail || e.message || "Split failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!item) return null;

  const hasSpend = (item.material_spend || 0) + (item.tooling_spend || 0) + (item.gauging_spend || 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[620px] max-w-[95vw] p-0 overflow-hidden flex flex-col max-h-[90vh]"
        data-testid="split-line-dialog"
      >
        {/* Accent rail */}
        <div className="h-[3px] w-full shrink-0 bg-emerald-500" />

        {/* Header */}
        <DialogHeader className="px-5 pt-4 pb-3 shrink-0 border-b border-[hsl(var(--border))]/50">
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-500/10 shrink-0">
              <Scissors className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </span>
            Split Line {item.line_number}
          </DialogTitle>
          <DialogDescription className="text-[11px] mt-1 truncate text-[hsl(var(--muted-foreground))]">
            <span className="font-mono-data font-bold text-[hsl(var(--foreground))]">{item.part_number || "—"}</span>
            {item.part_number && item.item_description ? " · " : ""}
            <span className="truncate">{item.item_description}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Input row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] block mb-1.5">
                Quantity to split
              </label>
              <input
                type="number"
                min={1}
                max={parentQty - 1}
                step={1}
                value={splitQty}
                onChange={e => setSplitQty(e.target.value)}
                placeholder={`1 – ${Math.max(1, parentQty - 1)}`}
                className="w-full rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-1.5 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                data-testid="split-qty-input"
              />
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                Original has <span className="font-mono-data font-bold">{parentQty}</span>
                {qty > 0 && qty >= parentQty && (
                  <span className="text-rose-500 ml-1">— must be less than {parentQty}</span>
                )}
              </p>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] block mb-1.5">
                New line number
              </label>
              <input
                type="text"
                value={newLineNumber}
                onChange={e => setNewLineNumber(e.target.value)}
                className={`w-full rounded-md border px-3 py-1.5 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-emerald-500/40
                  ${lineCollides
                    ? "border-rose-500/60 bg-rose-500/5 focus:ring-rose-500/40"
                    : "border-[hsl(var(--border))] bg-[hsl(var(--background))]"}`}
                data-testid="split-line-number-input"
              />
              {lineCollides && (
                <p className="text-[10px] text-rose-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3 shrink-0" />
                  Line {newLineNumber} already exists for this PO
                </p>
              )}
              {!lineCollides && newLineNumber.trim() && (
                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Auto-filled · edit if needed</p>
              )}
            </div>
          </div>

          {/* Live preview */}
          {qty > 0 && qty < parentQty && (
            <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-3">
              <p className="text-[9px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">
                Preview · money is conserved
              </p>
              <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
                {/* Original after split */}
                <PreviewColumn
                  label={`Line ${item.line_number} (after)`}
                  qty={newParentQty}
                  value={newParentValue}
                  material={newParentMaterial}
                  tooling={newParentTooling}
                  gauging={newParentGauging}
                  hasSpend={hasSpend}
                  tone="muted"
                />
                <div className="flex items-center justify-center">
                  <ArrowRight className="w-4 h-4 text-emerald-500/60" />
                </div>
                {/* New child line */}
                <PreviewColumn
                  label={`Line ${newLineNumber || "…"} (new)`}
                  qty={qty}
                  value={childValue}
                  material={childMaterial}
                  tooling={childTooling}
                  gauging={childGauging}
                  hasSpend={hasSpend}
                  tone="emerald"
                />
              </div>
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-3 text-center">
                ⓘ Any rounding remainder stays on the original line.
              </p>
            </div>
          )}

          {/* API error */}
          {apiError && (
            <p className="text-[11px] text-rose-500 flex items-center gap-1.5" data-testid="split-api-error">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />{apiError}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 flex items-center justify-between gap-2 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 shrink-0">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            data-testid="split-close-btn"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            data-testid="split-submit-btn"
          >
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            Create split line
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviewColumn({ label, qty, value, material, tooling, gauging, hasSpend, tone }) {
  const accent = tone === "emerald" ? "#10b981" : "hsl(var(--foreground))";
  return (
    <div className="space-y-1">
      <p className="text-[9px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] truncate">{label}</p>
      <p className="text-sm font-bold font-mono-data tabular-nums" style={{ color: accent }}>
        Qty: {qty}
      </p>
      <p className="text-[11px] font-mono-data text-[hsl(var(--foreground))]">
        Value: {fmtMoney(value)}
      </p>
      {hasSpend && (
        <p className="text-[10px] font-mono-data text-[hsl(var(--muted-foreground))]">
          M {fmtMoney(material)} · T {fmtMoney(tooling)} · G {fmtMoney(gauging)}
        </p>
      )}
    </div>
  );
}
